import { ChangeDetectionStrategy, Component, EventEmitter, Output, Input, OnDestroy, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService, CreateUserRequest } from '../shared/services/users.service';

@Component({
  selector: 'app-add-user-modal',
  templateUrl: './add-user-modal.component.html',
  styleUrls: ['./add-user-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule]
})
export class AddUserModalComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly usersService = inject(UsersService);

  @Output() closeModal = new EventEmitter<void>();
  @Output() userAdded = new EventEmitter<void>();
  @Input() tenantId: string = '';
  @Input() siteInstanceKey: string = '';

  userForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    displayName: ['', [Validators.required, Validators.maxLength(100)]],
    entities: [false],
    invoices: [false],
    assets: [false],
    messages: [false],
    shares: [false],
    information: [false],
    banking: [false],
    loans: [false],
    tasks: [false],
    relatedParties: [false],
    investments: [false],
    documents: [false],
    requests: [false]
  });
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  private readonly destroy$ = new Subject<void>();

  onSubmit() {
    if (this.userForm.valid && !this.isLoading()) {
      this.isLoading.set(true);
      this.error.set(null);

      const formValue = this.userForm.value;
      const createUserRequest: CreateUserRequest = {
        emailAddress: formValue.email,
        displayName: formValue.displayName,
        authorizations: {
          entities: formValue.entities,
          invoices: formValue.invoices,
          assets: formValue.assets,
          messages: formValue.messages,
          shares: formValue.shares,
          information: formValue.information,
          banking: formValue.banking,
          loans: formValue.loans,
          tasks: formValue.tasks,
          relatedParties: formValue.relatedParties,
          investments: formValue.investments,
          documents: formValue.documents,
          requests: formValue.requests
        },
        tenantId: this.tenantId,
        siteInstanceKey: this.siteInstanceKey
      };

      this.usersService.createUser(createUserRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.isLoading.set(false);

            // Show message if user existed in Entra ID
            if (response.userExistedInEntraId) {
              alert('This user already has an account in Microsoft Entra ID. They have been auto-enabled to log in to the portal. Access will be ready within 1 minute.');
            }

            this.userAdded.emit();
            this.closeModal.emit();
          },
          error: (error) => {
            console.error('Error creating user:', error);
            this.error.set(error.message || 'Failed to create user');
            this.isLoading.set(false);
          }
        });
    }
  }

  onCancel() {
    this.closeModal.emit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
